import React, { useState } from 'react';
import { get } from 'lodash';
import { Badge, Button, Spinner } from 'flowbite-react';
import { useToast } from 'src/context/ToastProvider';
import { HiOutlineStar, HiStar } from 'react-icons/hi';
import { useMutation, useQueryClient } from 'react-query';
import {
	hireAssessmentCandidate,
	rejectAssessmentCandidate,
	shortlistCampusHiringCandidate,
} from 'src/apis/campusHiring';
import {
	CampusApplicationDocument,
	CampusApplicationStatus,
	CampusRoundStatus,
	CampusRoundType,
	CampusUserWithApplication,
	DriveInviteeStatus,
} from 'types';
import { MenuItem, Popover, useMediaQuery } from '@mui/material';
import { useThemeContext } from 'src/context/ThemeProvider';
import { MenuOptionsType } from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import { IoChevronDown } from 'react-icons/io5';
import classNames from 'classnames';
import { Drawer } from 'src/components/shared/Drawer';
import CampusUserInterview from './campusInterviewComponents/CampusUserInterview';
import CampusCancelInterview from './campusInterviewComponents/CampusCancelInterview';
import { useNavigate } from 'react-router-dom';
import CampusInviteJobSeekerForDrive from './campusInterviewComponents/CampusInviteJobSeekerForDrive';
import dayjs from 'dayjs';
import { LightTooltip } from 'src/components/recruiterPositions/RecruiterCandidateCardHeader';

enum ContentTypeEnum {
	INTERVIEW = 'interview',
	RESCHEDULE = 'reschedule',
	CANCEL = 'cancel',
	PLACEMENT_DRIVE = 'placementDrive',
}

type ContentDrawerStateType = {
	contentType: ContentTypeEnum;
	heading?: string | React.ReactNode;
	maxWidth?: string;
} | null;

const CampusCandidateActionButton = ({
	ctaText,
	disable,
	studentData,
	handleCTAClick,
	showCandidateName,
	showShortlistingButton,
	totalShortlistedCandidates,
	showHriingAssessmentUsersOnly,
}: {
	ctaText?: string;
	disable?: boolean;
	showCandidateName?: boolean;
	showShortlistingButton?: boolean;
	studentData: CampusUserWithApplication;
	totalShortlistedCandidates?: number;
	showHriingAssessmentUsersOnly?: boolean;
	handleCTAClick?: (e: React.MouseEvent<HTMLElement>, openMenu: (e: React.MouseEvent<HTMLElement>) => void) => void;
}) => {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { themeMode } = useThemeContext();
	const [isLoading, setIsLoading] = useState(false);
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openContentDrawer, setOpenContentDrawer] = React.useState<ContentDrawerStateType>(null);

	const open = Boolean(anchorEl);
	const isMobile = useMediaQuery('(max-width:960px)');
	const id = open ? 'simple-popover' : undefined;
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const getCampusHiringApplicationsWithCandidatesState = queryClient.getQueryState([
		'getCampusHiringApplicationsWithCandidates',
	]);

	const getCampusHiringCandidatesState = queryClient.getQueryState(['getCampusHiringCandidates']);

	const interviewRound = (studentData?.application as CampusApplicationDocument)?.rounds.find(
		e => e?.roundStatus === CampusRoundStatus.STARTED && e?.roundType === CampusRoundType.INTERVIEW,
	);

	const isStudentHired = studentData?.application?.status === CampusApplicationStatus.HIRED;
	const isStudentRejected = studentData?.application?.status === CampusApplicationStatus.REJECTED;

	const acceptedDrives = studentData?.invitedToDrives?.filter(e => e?.status === DriveInviteeStatus.ACCEPT);

	const shortlistCampusHiringCandidateFn = useMutation(
		['shortlistCampusHiringCandidate'],
		shortlistCampusHiringCandidate,
		{
			onSuccess(data) {
				setIsLoading(true);
				queryClient
					.refetchQueries(['getCampusHiringCandidates'])
					.then(el => {
						setIsLoading(false);
					})
					.catch(err => setIsLoading(false));
				queryClient.refetchQueries({
					queryKey: ['getCampusHiringApplicationsWithCandidates'],
					exact: false, // This allows refetching of all matching queries
				});

				if (
					data?.campusApplication?.status === CampusApplicationStatus.SHORTLISTED &&
					totalShortlistedCandidates === 0
				) {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
				toast({
					title: 'Candidate shortlisted successfully',
					type: 'success',
				});
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);

	const hireCampusCandidateFn = useMutation(['hireCampusCandidate'], hireAssessmentCandidate, {
		onSuccess(data) {
			queryClient.refetchQueries(['getCampusHiringCandidates']);
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			toast({
				title: 'Status updated successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const rejectCampusCandidateFn = useMutation(['rejectCampusCandidate'], rejectAssessmentCandidate, {
		onSuccess(data) {
			queryClient.refetchQueries(['getCampusHiringCandidates']);
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			toast({
				title: 'Status updated successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const menuOptions = [
		{
			hide: showHriingAssessmentUsersOnly,
			label: 'Participate in Placement Drive',
			onClick: () => navigate('/upcoming-drives'),
		},
		{
			hide: showHriingAssessmentUsersOnly,
			label: 'Invite for Placement Drive',
			onClick: () => {
				setOpenContentDrawer({
					contentType: ContentTypeEnum.PLACEMENT_DRIVE,
				});
			},
		},
		{
			label: 'Schedule Interview',
			hide: !!interviewRound,
			onClick: () => {
				setOpenContentDrawer({
					contentType: ContentTypeEnum.INTERVIEW,
					maxWidth: '640px',
				});
			},
		},
		{
			label: 'Reschedule Interview',
			hide: !interviewRound,
			onClick: () => {
				setOpenContentDrawer({
					contentType: ContentTypeEnum.RESCHEDULE,
					maxWidth: '640px',
				});
			},
		},
		{
			label: 'Cancel Interview',
			hide: !interviewRound,
			onClick: () => {
				setOpenContentDrawer({
					contentType: ContentTypeEnum.CANCEL,
				});
			},
		},
		{
			label: 'Hire Candidate',
			hide: !showHriingAssessmentUsersOnly,
			onClick: () => {
				hireCampusCandidateFn.mutate({
					candidateIds: [studentData?._id],
				});
			},
		},
		{
			label: 'Reject Candidate',
			hide: !showHriingAssessmentUsersOnly,
			onClick: () => {
				rejectCampusCandidateFn.mutate({
					candidateIds: [studentData?._id],
				});
			},
		},
	];

	const getDrawerContentData = (contentType: string) => {
		switch (contentType) {
			case ContentTypeEnum.INTERVIEW:
				return (
					<CampusUserInterview
						studentData={{ ...studentData, name: showCandidateName ? studentData?.name : '' }}
						onFinish={() => setOpenContentDrawer(null)}
					/>
				);
			case ContentTypeEnum.RESCHEDULE:
				return (
					<CampusUserInterview
						isReschedule={true}
						studentData={{ ...studentData, name: showCandidateName ? studentData?.name : '' }}
						ongoingRound={(studentData?.application as CampusApplicationDocument)?.rounds.find(
							e => e?.roundStatus === CampusRoundStatus.STARTED && e?.roundType === CampusRoundType.INTERVIEW,
						)}
						onFinish={() => setOpenContentDrawer(null)}
					/>
				);
			case ContentTypeEnum.CANCEL:
				return (
					<CampusCancelInterview
						studentData={{ ...studentData, name: showCandidateName ? studentData?.name : '' }}
						onFinish={() => setOpenContentDrawer(null)}
					/>
				);
			case ContentTypeEnum.PLACEMENT_DRIVE:
				return (
					<CampusInviteJobSeekerForDrive
						jobseekers={[{ ...studentData, name: showCandidateName ? studentData?.name : '' }]}
						onFinish={() => setOpenContentDrawer(null)}
					/>
				);
			default:
				return null;
		}
	};

	return isStudentHired || isStudentRejected ? (
		<div>
			<Badge
				color={isStudentHired ? 'success' : isStudentRejected ? 'failure' : 'warning'}
				className="px-[14.4px] text-center w-fit inline-block rounded-[7.2px] font-[500] text-base"
			>
				{isStudentHired ? 'Hired' : isStudentRejected ? 'Rejected' : 'NA'}
			</Badge>
		</div>
	) : showShortlistingButton ? (
		<>
			{studentData?.application?.status === CampusApplicationStatus.SHORTLISTED ? (
				<div className="flex gap-1 justify-end items-center w-full">
					<Button color="purplePlaced" className="[&_span]:!p-0 !px-[16px] !py-[8px] border-[#E5E7EB]">
						<HiStar className="h-[16px] w-[16px] mr-[8px] text-[#FFFFFF]" />
						Shortlisted
					</Button>
				</div>
			) : (
				<div className="flex gap-1 justify-end items-center w-full">
					<Button
						color="light"
						disabled={
							shortlistCampusHiringCandidateFn?.isLoading ||
							getCampusHiringApplicationsWithCandidatesState?.isFetching ||
							getCampusHiringCandidatesState?.isFetching ||
							isLoading
						}
						onClick={() => shortlistCampusHiringCandidateFn.mutate({ candidateId: studentData?._id })}
						className="[&_span]:!p-0 !px-[16px] !py-[8px] bg-[#FAFAFC] border-[#7777E7] text-[#7777E7]"
					>
						{shortlistCampusHiringCandidateFn?.isLoading ||
							getCampusHiringApplicationsWithCandidatesState?.isFetching ||
							getCampusHiringCandidatesState?.isFetching ||
							isLoading ? (
							<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
								<Spinner className="float-right" />
							</div>
						) : null}
						<HiOutlineStar className="mr-[8px]" />
						Shortlist
					</Button>
				</div>
			)}
		</>
	) : (
		<>
			{studentData?.invitedToDrives?.length ? (
				<div className="flex flex-col items-center gap-2 justify-center font-inter text-center mb-1">
					{acceptedDrives?.length ? (
						<LightTooltip
							light={!(themeMode === 'dark')}
							title={
								<div className="p-2 text-xs text-center text-[#555770] font-[500]">
									{acceptedDrives?.map(e => (
										<div className="" key={e?._id}>
											{e?.drive?.city},
											{e?.drive?.driveEndDate && e?.drive?.driveEndDate !== "" && e?.drive?.driveEndDate !== null ? `${dayjs(e?.drive?.driveDate).format('Do MMM')} - ${dayjs(e?.drive?.driveEndDate).format('Do MMM')}` : `${dayjs(e?.drive?.driveDate).format('Do MMM')}`}
										</div>
									))}
								</div>
							}
							placement="bottom"
							arrow={true}
							componentsProps={{
								tooltip: {
									sx: {
										border: '0px !important',
										borderRadius: '4px !important',
										boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
									},
								},
							}}
						>
							<div className="text-[#555770] text-xs font-inter font-[500] border-b border-dashed border-[#555770]">
								{acceptedDrives?.length} Accepted {acceptedDrives?.length > 1 ? 'Drives' : 'Drive'}
							</div>
						</LightTooltip>
					) : null}

					{studentData?.invitedToDrives?.length ? (
						<LightTooltip
							light={!(themeMode === 'dark')}
							title={
								<div className="p-2 text-xs text-center text-[#555770] font-[500]">
									{studentData?.invitedToDrives?.map(e => (
										<div className="" key={e?._id}>
											{e?.drive?.city}, {e?.drive?.driveEndDate ? `${dayjs(e?.drive?.driveDate).format('Do MMM')} - ${dayjs(e?.drive?.driveEndDate).format('Do MMM')}` : `${dayjs(e?.drive?.driveDate).format('Do MMM')}`}
										</div>
									))}
								</div>
							}
							placement="bottom"
							arrow={true}
							componentsProps={{
								tooltip: {
									sx: {
										border: '0px !important',
										borderRadius: '4px !important',
										boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
									},
								},
							}}
						>
							<div className="!rounded-[6px] bg-[#EDEBFE] px-[10px] text-[#5521B5] text-xs text-[500] py-1">
								Invited for {studentData?.invitedToDrives?.length > 1 ? 'Drives' : 'Drive'}
							</div>
						</LightTooltip>
					) : null}
				</div>
			) : null}

			{interviewRound ? (
				<div className="flex flex-col items-center gap-2 justify-center font-inter text-center">
					<div className="!rounded-[6px] bg-[#DEF7EC] px-[10px] text-[#03543F] text-xs text-[500] py-1">
						Interview Scheduled
					</div>
					<div className="text-[#28293D] text-xs font-[500] pb-1">
						{dayjs(interviewRound?.startTime).format('hh:mma')}, {dayjs(interviewRound?.startTime).format('DD MMM')}
					</div>
				</div>
			) : null}

			<Button color="purplePlaced" disabled={disable} className="ml-auto [&_span]:!p-0 !p-0 border-[#E5E7EB]">
				<p
					className="px-[10px] py-[8px]"
					onClick={e => {
						handleClose();
						if (interviewRound) {
							setOpenContentDrawer({
								contentType: ContentTypeEnum.RESCHEDULE,
								maxWidth: '640px',
							});
						} else if (showHriingAssessmentUsersOnly) {
							setOpenContentDrawer({
								contentType: ContentTypeEnum.INTERVIEW,
								maxWidth: '640px',
							});
						} else {
							setOpenContentDrawer({
								contentType: ContentTypeEnum.PLACEMENT_DRIVE,
							});
						}
						handleCTAClick && handleCTAClick(e, handleClick);
					}}
				>
					{interviewRound ? 'Reschedule Interview' : ctaText}
				</p>
				<span
					aria-haspopup="true"
					aria-describedby={id}
					aria-expanded={open ? 'true' : undefined}
					onClick={handleClick}
					className="border-l border-[#E5E7EB]"
				>
					<IoChevronDown
						className={classNames('w-[34px] h-[34px] px-[8px] transition-transform', open ? 'rotate-[180deg]' : '')}
					/>
				</span>

				<Popover
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					onClickCapture={() => {
						handleClose();
					}}
					sx={{
						'&>.MuiPaper-root': {
							borderRadius: '8px',
							background: themeMode === 'dark' ? '#18222D' : 'white',
							boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
						},
						'&>.MuiPaper-root>.MuiButtonBase-root': {
							minHeight: '40px',
						},
					}}
				>
					{menuOptions &&
						menuOptions
							.filter(el => !el?.hide)
							.map((menuOption: MenuOptionsType, index: number) => {
								return (
									<MenuItem key={index} className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'} {...menuOption}>
										{menuOption.label}
									</MenuItem>
								);
							})}
				</Popover>
			</Button>

			<Drawer
				fullWidth={true}
				minHeight="fit-content"
				isModal={!isMobile}
				isOpen={!!openContentDrawer}
				header={<></>}
				modalMaxWidth={openContentDrawer?.maxWidth ? openContentDrawer?.maxWidth : undefined}
				body={<div className="p-4">{getDrawerContentData(openContentDrawer?.contentType || '')}</div>}
				onClose={() => {
					setOpenContentDrawer(null);
				}}
			/>
		</>
	);
};

export default CampusCandidateActionButton;
